import {
  type MutationKey,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useContext } from 'react';
import { AlertContext } from '../contexts/alert-context';
import type { CreateOrder, Order } from '../data/order';
import { OrderStatus } from '../data/order';
import type { OrderItem } from '../data/order-item';
import type { OrderOptions } from '../data/order-options';
import type { Store } from '../data/store';
import type { Vendor } from '../data/vendor';
import { compare } from '../helpers/column-sort';
import { ApiResponseError } from '../helpers/fetch-wrapper';
import { ordersQueryOptions, writeOrderCache } from '../query/query-options';
import { storeLayout } from '../router';
import { classifyOrderConflict, type ItemUpdate } from '../services/orders';
import { useConnectionStatus } from '../websocket/use-connection-status';
import { useStoreWebSocket } from '../websocket/use-store-websocket';

// Every error the fetch wrapper raises for an HTTP status is an
// ApiResponseError (the version-conflict error extends it); anything else —
// a TypeError('Failed to fetch'), an abort — means the request never got an
// answer.
const serverAnswered = (error: Error): boolean => error instanceof ApiResponseError;

// While the socket is open, order_updated pushes keep the list current
// (useOrdersListEventListener) and its 3-hour staleTime is what makes
// navigating back to the list instant. While the socket is CLOSED no push can
// arrive, and three hours is far too long to show a wrong list — so degraded
// mode polls. Five minutes rather than the edit page's 60s: the list is a
// glanced-at overview, not the surface someone is typing into.
// refetchIntervalInBackground defaults to false, so a hidden tab does not poll
// either; the provider force-reconnects the moment it is foregrounded.
export const SOCKET_DOWN_POLL_MS = 5 * 60_000;

export function useOrders() {
  const { addErrorAlert } = useContext(AlertContext);
  const { store, orderService } = storeLayout.useRouteContext();
  const connection = useConnectionStatus();

  const { data: orders = [], isLoading } = useQuery({
    ...ordersQueryOptions(store.id),
    refetchInterval: connection === 'closed' ? SOCKET_DOWN_POLL_MS : false,
    queryFn: () => {
      return orderService.getOrdersByStore(store.id).catch((error: Error) => {
        addErrorAlert(`Invalid orders: ${error.message}`, { cause: error });

        throw error;
      });
    },
  });

  return {
    orders,
    // Only show loading if we don't have data AND we're actually loading (not just fetching)
    loading: orders.length === 0 && isLoading,
  };
}

export function useDraftOrders() {
  const { orders, loading } = useOrders();

  const draftOrdersByVendor = useCallback(
    (vendorId: string) => {
      return orders
        .filter((order) => order.vendor_id === vendorId && order.status === OrderStatus.Draft)
        .sort((a, b) => compare(new Date(b.updated_at), new Date(a.updated_at)));
    },
    [orders]
  );

  return { draftOrdersByVendor, loading };
}

export function useGetOrder() {
  const { addErrorAlert } = useContext(AlertContext);
  const { orderService } = storeLayout.useRouteContext();
  const queryClient = useQueryClient();

  return useCallback(
    (storeId: string, orderId: string) => {
      return orderService
        .getOrderByStore(storeId, orderId)
        .then((order) => {
          queryClient.setQueryData(
            ordersQueryOptions(storeId).queryKey,
            (oldOrders: Order[] = []) => {
              return oldOrders.map((oldOrder) => {
                if (oldOrder.id === order.id) {
                  return order;
                }

                return oldOrder;
              });
            }
          );

          return order;
        })
        .catch((error: Error) => {
          addErrorAlert(error.message, { cause: error });

          throw error;
        });
    },
    [orderService, addErrorAlert, queryClient]
  );
}

// The factory the six v1 order mutations below share (ORD-265). All six do the
// same four things around their own service call: cancel in-flight reads of the
// store's orders list, snapshot it, patch it optimistically, and put the
// snapshot back with one labelled toast if the call fails.
//
// `storeIdOf` rather than a `TVariables extends { storeId: string }` bound:
// useDeleteOrder's variables are an Order (`store_id`, not `storeId`),
// useBulkDeleteOrders' are an Order[] and it reads the store off the route
// context instead. Constraining the variables shape would mean rewriting those
// two hooks' public signatures — and their call sites — for the refactor's
// convenience.
//
// `optimisticUpdate` is optional because useCreateOrder has nothing to insert
// until the server hands back an ID; it still snapshots, so a failure rolls
// back whatever raced it. `onSuccess` is a plain passthrough for the two hooks
// that reconcile against the server's response.
function useOrderMutationV1<TVariables, TData>({
  mutationKey,
  mutationFn,
  storeIdOf,
  optimisticUpdate,
  onSuccess,
  errorLabel,
}: {
  mutationKey?: MutationKey;
  mutationFn: (variables: TVariables) => Promise<TData>;
  storeIdOf: (variables: TVariables) => string;
  optimisticUpdate?: (orders: Order[], variables: TVariables) => Order[];
  onSuccess?: (data: TData, variables: TVariables) => void;
  errorLabel: string;
}) {
  const queryClient = useQueryClient();
  const { addErrorAlert } = useContext(AlertContext);

  return useMutation({
    mutationKey,
    mutationFn,
    onMutate: async (variables: TVariables) => {
      const storeId = storeIdOf(variables);
      const { queryKey } = ordersQueryOptions(storeId);

      await queryClient.cancelQueries({ queryKey });

      const previousOrders = queryClient.getQueryData<Order[]>(queryKey);

      if (optimisticUpdate) {
        queryClient.setQueryData(queryKey, (oldOrders: Order[] = []) =>
          optimisticUpdate(oldOrders, variables)
        );
      }

      return { previousOrders, storeId };
    },
    onSuccess,
    onError: (err: Error, _variables, context) => {
      addErrorAlert(`${errorLabel}: ${err.message}`, { cause: err });

      if (context?.previousOrders) {
        queryClient.setQueryData(
          ordersQueryOptions(context.storeId).queryKey,
          context.previousOrders
        );
      }
    },
  });
}

export function useCreateOrder() {
  const queryClient = useQueryClient();
  const { orderService } = storeLayout.useRouteContext();

  const { mutateAsync: createOrder } = useOrderMutationV1({
    mutationFn: ({ storeId, order }: { storeId: string; order: CreateOrder }) => {
      return orderService.createOrder(storeId, order);
    },
    storeIdOf: ({ storeId }) => storeId,
    // No optimisticUpdate: we can't add the order to the list without the
    // server-generated ID. The factory still cancels in-flight reads and takes
    // the snapshot, which is what this hook always relied on.
    onSuccess: (newOrder: Order) => {
      // Add the new order with server-generated ID
      queryClient.setQueryData(
        ordersQueryOptions(newOrder.store_id).queryKey,
        (oldOrders: Order[] = []) => [...oldOrders, newOrder]
      );
    },
    errorLabel: 'Error creating order',
  });

  return (storeId: string, order: CreateOrder) => {
    return createOrder({ storeId, order });
  };
}

export function useMaybeCreateOrder() {
  const { draftOrdersByVendor } = useDraftOrders();
  const createOrder = useCreateOrder();
  const navigate = useNavigate();

  return async (store: Store, vendor: Vendor) => {
    const existingDraftOrders = draftOrdersByVendor(vendor.vendor_id);

    // If there are pending orders for this vendor we want the user to clean these up instead of creating another one
    if (existingDraftOrders.length > 0) {
      navigate({
        to: '/stores/$storeId/orders/cleanup',
        params: { storeId: store.id },
        search: { vendorId: vendor.vendor_id },
      });

      return;
    }

    const order = await createOrder(store.id, {
      vendor_id: vendor.vendor_id,
      items: [],
    });
    navigate({
      to: '/stores/$storeId/orders/$orderId/edit',
      params: { storeId: store.id, orderId: order.id },
    });
  };
}

export function useSaveOrderOptions() {
  const { orderService } = storeLayout.useRouteContext();

  const { mutateAsync: saveOrderOptions } = useOrderMutationV1({
    mutationFn: ({
      storeId,
      orderId,
      options,
    }: {
      storeId: string;
      orderId: string;
      options: OrderOptions;
    }) => {
      return orderService.saveOrderOptions(storeId, orderId, options);
    },
    storeIdOf: ({ storeId }) => storeId,
    optimisticUpdate: (orders, { orderId, options }) => {
      return orders.map((order) => {
        if (order.id === orderId) {
          return { ...order, options };
        }

        return order;
      });
    },
    errorLabel: 'Error saving order options',
  });

  // Keep this function since it has validation logic
  return (storeId: string, orderId: string, options: OrderOptions) => {
    return saveOrderOptions({ storeId, orderId, options });
  };
}

export function useSaveOrderItem() {
  const { orderService } = storeLayout.useRouteContext();

  const { mutateAsync: saveOrderItem } = useOrderMutationV1({
    mutationFn: ({
      storeId,
      orderId,
      orderItem,
    }: {
      storeId: string;
      orderId: string;
      orderItem: OrderItem;
    }) => {
      return orderService.updateOrderItem(storeId, orderId, orderItem);
    },
    storeIdOf: ({ storeId }) => storeId,
    optimisticUpdate: (orders, { orderId, orderItem }) => {
      return orders.map((order) => {
        if (order.id === orderId) {
          return {
            ...order,
            items: order.items.map((i) => (i.product.id === orderItem.product.id ? orderItem : i)),
          };
        }

        return order;
      });
    },
    errorLabel: 'Error saving order item',
  });

  return (storeId: string, order: Order, orderItem: OrderItem) => {
    return saveOrderItem({ storeId, orderId: order.id, orderItem });
  };
}

// Registered so the v2 editor can tell its own submit from one made in another
// tab: the server broadcasts the send before it answers, so the submitting
// tab's own order_updated push refetches the order as Sending while the review
// dialog is still up (see isOrderSubmitInFlight).
const ORDER_SUBMIT_MUTATION_KEY = ['orders', 'submit'] as const;

type OrderSubmitVariables = { storeId: string; order: Order; method: string };

// True while this tab's submit of `orderId` is awaiting its response.
export function isOrderSubmitInFlight(queryClient: QueryClient, orderId: string): boolean {
  return (
    queryClient.isMutating({
      mutationKey: ORDER_SUBMIT_MUTATION_KEY,
      predicate: (mutation) =>
        (mutation.state.variables as OrderSubmitVariables | undefined)?.order.id === orderId,
    }) > 0
  );
}

export function useSubmitOrder() {
  const queryClient = useQueryClient();
  const { orderService } = storeLayout.useRouteContext();

  const { mutateAsync: submitOrder } = useOrderMutationV1({
    mutationKey: ORDER_SUBMIT_MUTATION_KEY,
    mutationFn: ({ storeId, order, method }: OrderSubmitVariables) => {
      return orderService.submitOrder(storeId, order, method);
    },
    storeIdOf: ({ storeId }) => storeId,
    // Optimistically update order status (assuming it will be sent)
    optimisticUpdate: (orders, { order }) => {
      return orders.map((o) =>
        o.id === order.id
          ? { ...order, status: OrderStatus.Sending } // Assuming sending status
          : o
      );
    },
    onSuccess: (updatedOrder: Order, { storeId }) => {
      // Update with actual server response
      queryClient.setQueryData(ordersQueryOptions(storeId).queryKey, (oldOrders: Order[] = []) => {
        return oldOrders.map((o) => (o.id === updatedOrder.id ? updatedOrder : o));
      });
    },
    errorLabel: 'Error submitting order',
  });

  return (storeId: string, order: Order, method: string) => {
    return submitOrder({ storeId, order, method });
  };
}

export function useDownloadOrder() {
  const { orderService } = storeLayout.useRouteContext();
  const { addErrorAlert } = useContext(AlertContext);

  return useCallback(
    (storeId: string, orderId: string, part?: number) => {
      orderService.downloadOrder(storeId, orderId, part).catch((error: Error) => {
        addErrorAlert(`Error downloading order: ${error.message}`, { cause: error });
      });
    },
    [orderService, addErrorAlert]
  );
}

export function useDeleteOrder() {
  const { orderService } = storeLayout.useRouteContext();

  const { mutateAsync: deleteOrder } = useOrderMutationV1({
    mutationFn: (order: Order) => {
      return orderService.deleteOrder(order);
    },
    storeIdOf: (order) => order.store_id,
    // Optimistically remove the order
    optimisticUpdate: (orders, order) => orders.filter((o) => o.id !== order.id),
    errorLabel: 'Error deleting order',
  });

  return deleteOrder;
}

export function useBulkDeleteOrders() {
  const { store, orderService } = storeLayout.useRouteContext();

  const { mutateAsync: bulkDeleteOrders } = useOrderMutationV1({
    mutationFn: async (deleteOrders: Order[]) => {
      return Promise.all(deleteOrders.map((order) => orderService.deleteOrder(order)));
    },
    // The variables are the orders to delete, which carry no single store —
    // this is the store the page is on, same as before the extraction.
    storeIdOf: () => store.id,
    // Optimistically remove orders
    optimisticUpdate: (orders, deleteOrders) =>
      orders.filter((o) => !deleteOrders.some((e) => e.id === o.id)),
    errorLabel: 'Error deleting orders',
  });

  return bulkDeleteOrders;
}

// The factory the three v2 order mutations share (ORD-265). Deliberately does
// NOT merge with useOrderMutationV1 above, and does not repeat its shape
// (cancel reads, snapshot, patch optimistically, roll back on error): on the
// v2 path the response IS the authoritative order, thanks to the server-owned
// version counter, so there is nothing to snapshot or roll back. What makes a typed edit visible
// before the response lands is the pending-edit overlay in
// use-order-items-v2, not an optimistic cache patch here.
type OrderWriteVariables = { storeId: string; orderId: string };

// Every v2 order write registers under this key so the order_updated listener
// (hooks/use-order-event-listener.ts) can tell "my own write is still in
// flight" from "someone else changed the order" — the server broadcasts before
// it answers the request, so a tab's own push routinely beats its own response.
const ORDER_WRITE_V2_MUTATION_KEY = ['orders', 'write-v2'] as const;

// True while any v2 write (items, allocate, options) for `orderId` is awaiting
// its response. Lives here, next to the mutations, because it is the only
// place that knows both the key and the variables shape.
export function isOrderWriteInFlight(queryClient: QueryClient, orderId: string): boolean {
  return (
    queryClient.isMutating({
      mutationKey: ORDER_WRITE_V2_MUTATION_KEY,
      predicate: (mutation) =>
        (mutation.state.variables as OrderWriteVariables | undefined)?.orderId === orderId,
    }) > 0
  );
}

function useOrderMutationV2<TVariables extends OrderWriteVariables>(
  mutationFn: (variables: TVariables) => Promise<Order>,
  errorLabel: string
) {
  const queryClient = useQueryClient();
  const { addErrorAlert } = useContext(AlertContext);
  const { checkLiveness, ensureConnected } = useStoreWebSocket();

  return useMutation({
    mutationKey: ORDER_WRITE_V2_MUTATION_KEY,
    mutationFn,
    onSuccess: (order, { storeId, orderId }) => {
      // The v2 response IS the authoritative order. Write it to both caches
      // rather than invalidating: we already hold newer data than a refetch
      // would produce, and an invalidate here would race the WS push (MR3).
      // writeOrderCache applies the newerOrder version gate — items and
      // options edits run through separate mutations with independent
      // FlushGuards, so an earlier-dispatched PATCH resolving last must not
      // clobber the other's already-cached, newer response.
      writeOrderCache(queryClient, storeId, orderId, order);
      // The server answered, so the network is back: a socket still sleeping
      // out its backoff is replaced now, and the chip reads Saved on the save
      // instead of whenever partysocket next tried (ORD-462).
      ensureConnected();
    },
    onError: (error: Error) => {
      // A write's outcome is evidence about the socket too. One the server
      // never answered may mean the network is gone, and the provider only
      // probes every five minutes — without the check a save failing on a
      // dead network showed Saving… for minutes before the chip could say
      // Offline (Phase D drive, ORD-458). An HTTP error means the server
      // answered, which is the same evidence a success is (ORD-462).
      if (serverAnswered(error)) {
        ensureConnected();
      } else {
        checkLiveness();
      }

      // Both v2-order 409s are caller-owned recovery paths, not failures for
      // this generic toast to report: the overlay hook rebases onto the
      // version-conflict response, and navigates away with its own specific
      // alert on the not-a-draft one. Toasting either here would duplicate
      // that more specific handling. classifyOrderConflict is shared with
      // use-order-items-v2's handleFlushError so the two can't drift — and it
      // classifies on the body's `code`, so a 409 neither of them knows how to
      // recover from still lands here and gets reported.
      if (classifyOrderConflict(error) !== null) {
        return;
      }

      addErrorAlert(`${errorLabel}: ${error.message}`, { cause: error });
    },
  });
}

export function useBatchSetOrderItems() {
  const { orderService } = storeLayout.useRouteContext();

  return useOrderMutationV2(
    ({ storeId, orderId, updates }: { storeId: string; orderId: string; updates: ItemUpdate[] }) =>
      orderService.batchSetOrderItems(storeId, orderId, updates),
    'Error saving order items'
  );
}

export function useAllocateOrderDeal() {
  const { orderService } = storeLayout.useRouteContext();

  return useOrderMutationV2(
    ({
      storeId,
      orderId,
      familyId,
      cases,
    }: {
      storeId: string;
      orderId: string;
      familyId: string;
      cases: number;
    }) => orderService.allocateOrderDeal(storeId, orderId, familyId, cases),
    'Error allocating deal'
  );
}

export function useSetOrderOptionsV2() {
  const { orderService } = storeLayout.useRouteContext();

  return useOrderMutationV2(
    ({ storeId, orderId, options }: { storeId: string; orderId: string; options: OrderOptions }) =>
      orderService.setOrderOptions(storeId, orderId, options),
    'Error saving order settings'
  );
}
